/**
 * Protocolo de sincronización: operaciones, fusión campo a campo por HLC
 * (último escritor gana por campo) e idempotencia por `opId`.
 */
import { z } from 'zod';
import { ENTITIES, entitySchemas, isValidEntityId, type EntityName } from './entities.js';
import { isValidHLC } from './hlc.js';

export const PROTOCOL_VERSION = 1;

/** Un campo de una operación: [valor, hlc]. */
export type FieldWrite = [unknown, string];

export interface SyncOp {
  opId: string;
  entity: EntityName;
  id: string;
  fields: Record<string, FieldWrite>;
}

export interface StoredRecord {
  entity: EntityName;
  id: string;
  data: Record<string, unknown>;
  /** Marca HLC por campo. */
  hlc: Record<string, string>;
}

/** Registro tal como viaja en un pull. */
export interface PulledRecord extends StoredRecord {
  seq: number;
}

const fieldName = z.string().regex(/^[a-zA-Z][a-zA-Z0-9]{0,40}$/);

export const syncOpSchema = z.object({
  opId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  entity: z.enum(ENTITIES),
  id: z.string().min(1).max(64),
  fields: z
    .record(fieldName, z.tuple([z.unknown(), z.string().refine(isValidHLC, 'hlc no válido')]))
    .refine((f) => Object.keys(f).length > 0 && Object.keys(f).length <= 40, 'número de campos no válido'),
});

export const pushRequestSchema = z.object({
  protocol: z.literal(PROTOCOL_VERSION),
  deviceId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  ops: z.array(syncOpSchema).max(500),
});

export type PushRequest = z.infer<typeof pushRequestSchema>;

export type OpResultStatus = 'applied' | 'duplicate' | 'rejected';

export interface OpResult {
  opId: string;
  status: OpResultStatus;
  /** Código estable en caso de rechazo. */
  code?: 'invalid' | 'plan_limit' | 'clock_skew' | 'forbidden';
  detail?: string;
}

export interface PushResponse {
  results: OpResult[];
  cursor: number;
}

export interface PullResponse {
  records: PulledRecord[];
  cursor: number;
  hasMore: boolean;
}

export interface MergeResult {
  record: StoredRecord;
  changed: boolean;
}

/**
 * Fusiona una escritura sobre un registro existente. Un campo se sobrescribe
 * solo si la marca entrante es estrictamente mayor (FR-SYNC-04). La operación
 * es conmutativa, asociativa e idempotente.
 */
export function mergeFields(
  existing: StoredRecord | null,
  entity: EntityName,
  id: string,
  fields: Record<string, FieldWrite>,
): MergeResult {
  const data: Record<string, unknown> = { ...(existing?.data ?? {}) };
  const hlc: Record<string, string> = { ...(existing?.hlc ?? {}) };
  let changed = false;
  for (const [name, [value, stamp]] of Object.entries(fields)) {
    const current = hlc[name];
    // Con marcas idénticas (solo posible con un reloj mal usado) se desempata por
    // el valor serializado, para que la fusión siga siendo conmutativa.
    const wins =
      current === undefined ||
      stamp > current ||
      (stamp === current && JSON.stringify(value) > JSON.stringify(data[name]));
    if (wins) {
      data[name] = value;
      hlc[name] = stamp;
      changed = true;
    }
  }
  return { record: { entity, id, data, hlc }, changed };
}

/** Fusiona dos registros completos (por ejemplo, al recibir un pull). */
export function mergeRecords(local: StoredRecord | null, remote: StoredRecord): MergeResult {
  const fields: Record<string, FieldWrite> = {};
  for (const [k, v] of Object.entries(remote.data)) {
    const stamp = remote.hlc[k];
    if (stamp) fields[k] = [v, stamp];
  }
  return mergeFields(local, remote.entity, remote.id, fields);
}

export function maxHLCOf(record: StoredRecord): string | null {
  let max: string | null = null;
  for (const v of Object.values(record.hlc)) if (!max || v > max) max = v;
  return max;
}

export type ValidationOutcome = { ok: true; data: Record<string, unknown> } | { ok: false; detail: string };

/** Valida un registro completo tras la fusión. */
export function validateRecord(entity: EntityName, id: string, data: Record<string, unknown>): ValidationOutcome {
  if (!isValidEntityId(entity, id)) return { ok: false, detail: 'identificador no válido' };
  const res = entitySchemas[entity].safeParse(data);
  if (!res.success) {
    return { ok: false, detail: res.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ').slice(0, 300) };
  }
  // Se conservan solo los campos conocidos del esquema.
  return { ok: true, data: res.data as Record<string, unknown> };
}

/** Construye una operación a partir del estado completo de un registro (subida inicial). */
export function opFromRecord(opId: string, record: StoredRecord): SyncOp {
  const fields: Record<string, FieldWrite> = {};
  for (const [k, v] of Object.entries(record.data)) {
    const stamp = record.hlc[k];
    if (stamp) fields[k] = [v, stamp];
  }
  return { opId, entity: record.entity, id: record.id, fields };
}
